import { CommonModule } from '@angular/common';
import * as uuid from 'uuid';
import {
    AfterContentInit,
    Component,
    ElementRef,
    EventEmitter,
    Input,
    OnChanges,
    OnInit,
    Output,
    SimpleChanges,
    ViewChild,
    ViewEncapsulation
} from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatAutocompleteModule, MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { BehaviorSubject, map, Observable, startWith } from 'rxjs';

@Component({
    selector: 'app-details-input',
    standalone: true,
    imports: [CommonModule, ReactiveFormsModule, MatFormFieldModule, MatInputModule, MatIconModule, MatAutocompleteModule],
    templateUrl: './details-input.component.html',
    styleUrl: './details-input.component.scss',
    encapsulation: ViewEncapsulation.None
})
export class DetailsInputComponent implements OnInit, OnChanges {

    @Input() label?: string;
    @Input() placeholder: string | undefined;
    @Input() inputControl: FormControl = new FormControl('');
    @Input() errorMessage?: string;
    @Input() autoCompleteOptions?: { text: string; value: string }[];
    @Input() hideWarningIcon: boolean = false;

    @Output() inputBlur = new EventEmitter();
    @Output() inputKeyUp = new EventEmitter();

    @ViewChild('inputElement') inputElementRef!: ElementRef;

    id: string;
    icon = new BehaviorSubject<string>('');
    filteredOptions: Observable<typeof this.autoCompleteOptions>;
    isEditing = false;
    isDisabled = false;

    ngOnInit(): void {
        this.inputControl.valueChanges.subscribe(_ => {
            this.updateIcon();
        });
        this.id = uuid.v4();
    }


    ngOnChanges(changes: SimpleChanges): void {
        if (changes['inputControl']) {
            this.inputControl = changes['inputControl'].currentValue || new FormControl('');
            this.updateIcon();
            this.isDisabled = this.inputControl.disabled;

            if (this.inputControl.errors) {
                this.inputControl.markAsTouched();
            }
        }
    }

    onKeyUp(event: KeyboardEvent) {
        this.inputKeyUp.emit(event);
    }

    onMouseEnter() {
        if (this.isDisabled) {
            return;
        }
        if (!this.isEditing && this.inputControl.valid) {
            this.icon.next('edit');
        }
    }

    onMouseLeave() {
        if (this.isDisabled) {
            return;
        }
        if (!this.isEditing && this.inputControl.valid) {
            this.icon.next('');
        }
    }

    onFocus() {
        if (this.isDisabled) {
            return;
        }
        this.isEditing = true;
        this.icon.next('');

        this.filteredOptions = this.inputControl.valueChanges.pipe(
            startWith(this.inputControl.value),
            map(value => this.filter(value || ''))
        );
    }

    onBlur(event: FocusEvent) {
        if (this.isDisabled) {
            return;
        }
        this.isEditing = false;
        this.inputBlur.emit(event);
        this.updateIcon();
    }

    onSelectSearchValue(event: MatAutocompleteSelectedEvent) {
        this.inputElementRef.nativeElement.blur();
    }

    private updateIcon() {
        if (this.shouldShowWarningIcon()) {
            this.icon.next('warning');
        } else {
            this.icon.next('');
        }
    }

    private shouldShowWarningIcon() {
        /*
         * Basically for some input fields we only want to show the alert icon for validation errors other than 'required'
         * If `hideWarningIcon` is true we only want to show the warning icon for other errors.
         * Definitionally required will trigger when it is the only validation error, so we can check that the input
         * is invalid and does NOT have it to see if we should display the warning icon
         */
        return this.inputControl.invalid && !this.isEditing && !(this.hideWarningIcon && this.inputControl.hasError('required'));
    }

    private filter(value?: string): typeof this.autoCompleteOptions {
        const filterValue = value?.toString().toLowerCase();

        return filterValue && this.autoCompleteOptions
            ? this.autoCompleteOptions.filter(option => option.text.toLowerCase().includes(filterValue))
            : (this.autoCompleteOptions ?? []);
    }
}
